import { useEffect, useState } from 'react';
import SettlementModalShell from '../../common/SettlementModalShell.js';
import dashboardService from '../../../services/DashboardService.js';
import { fullCurrency } from '../../../utils/formatCurrency.js';

const money = (value) => fullCurrency(value || 0);

const day = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const sum = (rows, key) => (rows || []).reduce((total, row) => total + Number(row[key] || 0), 0);

const Head = ({ columns }) => (
  <thead className="bg-gray-50 text-gray-600 sticky top-0">
    <tr>
      {columns.map((column) => (
        <th
          key={column}
          className={`px-3 py-2 font-medium ${column === 'GC' || column === 'Mode' || column === 'Booked' || column === 'From' || column === 'To' ? 'text-left' : 'text-right'}`}
        >
          {column}
        </th>
      ))}
    </tr>
  </thead>
);

const TotalRow = ({ label, span, cells }) => (
  <tr className="bg-gray-50 font-semibold text-gray-900">
    <td className="px-3 py-2" colSpan={span}>{label}</td>
    {cells.map((cell) => (
      <td key={cell.key} className={`px-3 py-2 text-right tabular-nums ${cell.className || ''}`}>{money(cell.value)}</td>
    ))}
  </tr>
);

const Section = ({ title, hint, count, children }) => (
  <section className="mb-5">
    <div className="flex items-baseline justify-between gap-3 mb-2">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <span className="text-xs text-gray-500">{count} pending</span>
    </div>
    <p className="text-xs text-gray-500 mb-2">{hint}</p>
    <div className="overflow-x-auto rounded-lg border border-gray-100">
      <table className="w-full min-w-[720px] text-sm">{children}</table>
    </div>
  </section>
);

const ReturnBreakdownModal = ({
  kind,
  userId,
  clientId,
  dateRange = 'all_time',
  customStart = '',
  customEnd = '',
  onClose,
}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const params = { date_range: dateRange || 'all_time' };
    if (dateRange === 'custom' && customStart && customEnd) {
      params.from_date = customStart;
      params.to_date = customEnd;
    }
    const request = kind === 'client'
      ? dashboardService.getCreditClientDetail(clientId, params)
      : dashboardService.getAgentReturnDetail(userId, params);
    request
      .then((res) => {
        if (!cancelled) setData(res?.data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Could not load these records');
      });
    return () => { cancelled = true; };
  }, [kind, userId, clientId, dateRange, customStart, customEnd]);

  const agent = data?.agent;
  const client = data?.client;
  const title = kind === 'client' ? (client?.name || 'Credit client') : (agent?.name || 'Agent');
  const bookings = data?.bookings || [];
  const toPay = data?.to_pay || [];
  const shipments = data?.shipments || [];
  const lineReturn = sum(bookings, 'to_return') + sum(toPay, 'to_return');
  const stillDue = Number(agent?.still_due || 0);

  return (
    <SettlementModalShell
      title={title}
      subtitle={kind === 'client'
        ? (client?.company_name || 'Pending credit shipments')
        : 'Pending records. Booking keeps loading and booking commission. To-Pay keeps unloading and commission.'}
      onClose={onClose}
      size="xl"
      footer={data ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            {kind === 'client'
              ? `${shipments.length} pending credit record${shipments.length === 1 ? '' : 's'}`
              : `${bookings.length + toPay.length} pending record${bookings.length + toPay.length === 1 ? '' : 's'}`}
          </p>
          <p className="text-sm font-semibold text-gray-900">
            {kind === 'client'
              ? `Outstanding ${money(client?.outstanding)}`
              : `Total to return ${money(Math.abs(lineReturn - stillDue) > 1 ? stillDue : lineReturn)}`}
          </p>
        </div>
      ) : null}
    >
      {!data && !error ? <p className="text-sm text-gray-400 py-8 text-center">Loading pending records…</p> : null}
      {error ? <p className="text-sm text-rose-700 py-8 text-center">{error}</p> : null}

      {kind === 'agent' && data ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            <Chip label="Paid" value={agent?.paid_total} />
            <Chip label="Loading" value={agent?.loading} />
            <Chip label="Booking commission" value={agent?.booking_commission} />
            <Chip label="To-Pay" value={agent?.topay_total} />
            <Chip label="Unloading" value={agent?.unloading} />
            <Chip label="Delivery commission" value={agent?.delivery_commission} />
            <Chip label="Returned" value={agent?.returned} />
            <Chip label="Still due" value={agent?.still_due} strong />
          </div>

          <Section
            title="Booking"
            hint="Amount, then loading and booking commission kept by this agent. To return is what is left for the company."
            count={bookings.length}
          >
            <Head columns={['GC', 'Booked', 'Mode', 'Amount', 'Loading', 'Commission', 'To return']} />
            <tbody className="divide-y divide-gray-100">
              {bookings.length ? bookings.map((row) => (
                <tr key={row.shipment_id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{row.shipment_id}</td>
                  <td className="px-3 py-2 text-gray-600">{day(row.booked_on)}</td>
                  <td className="px-3 py-2 text-gray-600">{row.mode}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.loading)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.commission)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{money(row.to_return)}</td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No pending booking records</td></tr>
              )}
              {bookings.length ? (
                <TotalRow
                  label="Total"
                  span={3}
                  cells={[
                    { key: 'amount', value: sum(bookings, 'amount') },
                    { key: 'loading', value: sum(bookings, 'loading') },
                    { key: 'commission', value: sum(bookings, 'commission') },
                    { key: 'to_return', value: sum(bookings, 'to_return'), className: 'font-semibold' },
                  ]}
                />
              ) : null}
            </tbody>
          </Section>

          <Section
            title="To-Pay"
            hint="Amount, then unloading and commission on this GC. To return is what is left for the company."
            count={toPay.length}
          >
            <Head columns={['GC', 'Booked', 'Amount', 'Unloading', 'Commission', 'To return']} />
            <tbody className="divide-y divide-gray-100">
              {toPay.length ? toPay.map((row) => (
                <tr key={row.shipment_id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{row.shipment_id}</td>
                  <td className="px-3 py-2 text-gray-600">{day(row.booked_on)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.unloading)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.commission)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{money(row.to_return)}</td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No pending To-Pay records</td></tr>
              )}
              {toPay.length ? (
                <TotalRow
                  label="Total"
                  span={2}
                  cells={[
                    { key: 'amount', value: sum(toPay, 'amount') },
                    { key: 'unloading', value: sum(toPay, 'unloading') },
                    { key: 'commission', value: sum(toPay, 'commission') },
                    { key: 'to_return', value: sum(toPay, 'to_return'), className: 'font-semibold' },
                  ]}
                />
              ) : null}
            </tbody>
          </Section>
        </>
      ) : null}

      {kind === 'client' && data ? (
        <>
          <div className="grid grid-cols-3 gap-2 mb-5">
            <Chip label="Billed" value={client?.billed} />
            <Chip label="Recovered" value={client?.recovered} />
            <Chip label="Outstanding" value={client?.outstanding} strong />
          </div>
          <Section
            title="Pending credit shipments"
            hint="Billed is the shipment total. Recovered is what is already paid. Outstanding is still with the client."
            count={shipments.length}
          >
            <Head columns={['GC', 'Booked', 'From', 'To', 'Billed', 'Recovered', 'Outstanding']} />
            <tbody className="divide-y divide-gray-100">
              {shipments.length ? shipments.map((row) => (
                <tr key={row.shipment_id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{row.shipment_id}</td>
                  <td className="px-3 py-2 text-gray-600">{day(row.booked_on)}</td>
                  <td className="px-3 py-2 text-gray-600">{row.sender_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{row.receiver_name || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.billed)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.recovered)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{money(row.outstanding)}</td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No pending credit records</td></tr>
              )}
              {shipments.length ? (
                <TotalRow
                  label="Total"
                  span={4}
                  cells={[
                    { key: 'billed', value: sum(shipments, 'billed') },
                    { key: 'recovered', value: sum(shipments, 'recovered') },
                    { key: 'outstanding', value: sum(shipments, 'outstanding'), className: 'font-semibold' },
                  ]}
                />
              ) : null}
            </tbody>
          </Section>
        </>
      ) : null}
    </SettlementModalShell>
  );
};

const Chip = ({ label, value, strong = false }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
    <p className="text-[11px] text-gray-500">{label}</p>
    <p className={`mt-0.5 text-sm tabular-nums ${strong ? 'font-semibold text-amber-800' : 'font-medium text-gray-900'}`}>{money(value)}</p>
  </div>
);

export default ReturnBreakdownModal;
